import { Header } from "@/components/Header";
import Footer from "@/components/Footer";
import { useEffect } from "react";
import { useTheme } from "next-themes";
import { Helmet } from "react-helmet-async";

const Docs = () => {
  const { setTheme } = useTheme();

  useEffect(() => {
    setTheme("light");
  }, [setTheme]);

  return (
    <div className="min-h-screen flex flex-col font-sans">
      <Helmet>
        <title>Documentation - How to Use MacroVault Macros</title>
        <meta name="description" content="Learn how to copy, install, and use VBA macros from MacroVault in Excel. Step-by-step guide for beginners." />
        <link rel="canonical" href="https://findmacros.com/docs" />
      </Helmet>
      <Header variant="default" />
      
      <main className="flex-1 pt-20 pb-16">
        <div className="max-w-[800px] mx-auto px-6">
          {/* H1 Title */}
          <h1 className="text-[36px] font-semibold mb-16 text-foreground" style={{ fontFamily: 'Inter, sans-serif' }}>
            How to use MacroVault macros in Excel
          </h1>
          
          <p className="text-base text-foreground/80 mb-16 leading-relaxed">
            Follow these simple steps to copy a macro from MacroVault into Excel, run it, and keep it organized. No VBA experience required.
          </p>

          {/* Before you start card */}
          <div className="mb-16 p-6 rounded-xl bg-muted/30">
            <h2 className="text-[24px] font-semibold mb-4 text-foreground">Before you start</h2>
            <ul className="space-y-2 text-foreground/80 list-disc pl-5">
              <li>You'll need Microsoft Excel installed on your computer.</li>
              <li>Macros must be enabled for VBA code to run.</li>
              <li>We recommend working in a copy of your file the first time you try a new macro.</li>
            </ul>
          </div>

          {/* Step 1 */}
          <div className="mb-16">
            <div className="flex items-start gap-10 mb-6">
              <div 
                className="flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center bg-primary text-primary-foreground font-semibold text-xl"
              >
                1
              </div>
              <div className="flex-1">
                <h2 className="text-[24px] font-semibold mb-4 text-foreground">Get a macro from MacroVault</h2>
                <p className="text-base text-foreground/80 mb-4 leading-relaxed">
                  Browse the MacroVault library and choose a macro that matches the task you want to automate.
                </p>
                <ul className="space-y-2 text-foreground/80 list-disc pl-5">
                  <li>Open the Browse page and click into any macro to view details.</li>
                  <li>Read the Description, Inputs, and What it does to confirm it fits your workflow.</li>
                  <li>When you're ready, choose how you want to grab the macro:
                    <ul className="ml-5 mt-1 space-y-1">
                      <li>Click <strong>Copy code</strong> to copy the VBA text, or</li>
                      <li>Click <strong>Download .bas file</strong> (if available) to import directly into Excel.</li>
                    </ul>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Step 2 */}
          <div className="mb-16">
            <div className="flex items-start gap-10 mb-6">
              <div 
                className="flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center bg-primary text-primary-foreground font-semibold text-xl"
              >
                2
              </div>
              <div className="flex-1">
                <h2 className="text-[24px] font-semibold mb-4 text-foreground">Add the macro to Excel</h2>
                <p className="text-base text-foreground/80 mb-6 leading-relaxed">
                  You can either paste the code or import the file. Both methods end up in the VBA editor.
                </p>
                
                <div className="mb-6">
                  <h3 className="text-lg font-semibold mb-3 text-foreground">Option A — Copy & paste (most common)</h3>
                  <ol className="space-y-2 text-foreground/80 list-decimal pl-5">
                    <li>Open your Excel workbook.</li>
                    <li>Press <strong>Alt + F11</strong> (Windows) or <strong>Fn + Option + F11</strong> (Mac) to open the VBA editor.</li>
                    <li>In the left pane ("Project"), right-click your workbook name:
                      <ul className="ml-5 mt-1 space-y-1 list-disc">
                        <li>Choose <strong>Insert → Module</strong>.</li>
                      </ul>
                    </li>
                    <li>Click inside the new module and paste the macro code you copied from MacroVault.</li>
                    <li>Press <strong>Ctrl + S</strong> (or <strong>Cmd + S</strong> on Mac) to save the VBA project.</li>
                  </ol>
                </div>

                <div className="mb-6">
                  <h3 className="text-lg font-semibold mb-3 text-foreground">Option B — Import a .bas file</h3>
                  <ol className="space-y-2 text-foreground/80 list-decimal pl-5">
                    <li>Open your Excel workbook.</li>
                    <li>Press <strong>Alt + F11</strong> / <strong>Fn + Option + F11</strong> to open the VBA editor.</li>
                    <li>In the menu bar, click <strong>File → Import File…</strong>.</li>
                    <li>Select the <strong>.bas file</strong> you downloaded from MacroVault and click <strong>Open</strong>.</li>
                    <li>A new module will appear with the macro already inside.</li>
                  </ol>
                </div>

                <div className="p-4 rounded-lg border border-border/20 bg-muted/30">
                  <p className="text-sm text-foreground/70">
                    If Excel warns you about macros, choose <strong>Enable Content</strong> so the code can run.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Step 3 */}
          <div className="mb-16">
            <div className="flex items-start gap-10 mb-6">
              <div 
                className="flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center bg-primary text-primary-foreground font-semibold text-xl"
              >
                3
              </div>
              <div className="flex-1">
                <h2 className="text-[24px] font-semibold mb-4 text-foreground">Run and manage your macro</h2>
                
                <div className="mb-6">
                  <h3 className="text-lg font-semibold mb-3 text-foreground">Running the macro</h3>
                  <ol className="space-y-2 text-foreground/80 list-decimal pl-5">
                    <li>Go back to Excel (close or minimize the VBA editor).</li>
                    <li>On the ribbon, go to <strong>View → Macros → View Macros</strong> (or <strong>Developer → Macros</strong> if you use the Developer tab).</li>
                    <li>Select the macro name you just added.</li>
                    <li>Click <strong>Run</strong>.</li>
                  </ol>
                </div>

                <div className="mb-6">
                  <h3 className="text-lg font-semibold mb-3 text-foreground">Optional: Make it easy to re-use</h3>
                  <ul className="space-y-2 text-foreground/80 list-disc pl-5">
                    <li>Create a button on your sheet (<strong>Insert → Shape</strong>), right-click it, and choose <strong>Assign Macro…</strong>.</li>
                    <li>Pick your macro so you can trigger it with one click.</li>
                    <li>Or add it to your Quick Access Toolbar for frequent use.</li>
                  </ul>
                </div>

                <div className="mb-6">
                  <h3 className="text-lg font-semibold mb-3 text-foreground">Managing & updating macros</h3>
                  <p className="text-base text-foreground/80 mb-3 leading-relaxed">
                    To edit a macro, open the VBA editor again and find the module under your workbook.
                  </p>
                  <p className="text-base text-foreground/80 mb-2 leading-relaxed">You can:</p>
                  <ul className="space-y-2 text-foreground/80 list-disc pl-5 mb-4">
                    <li>Rename modules (e.g., <code className="px-1.5 py-0.5 rounded bg-muted text-sm">mod_FormatReports</code>, <code className="px-1.5 py-0.5 rounded bg-muted text-sm">mod_DataCleanup</code>).</li>
                    <li>Keep one macro per module or group related ones together.</li>
                  </ul>
                  <p className="text-base text-foreground/80 mb-2 leading-relaxed">When MacroVault publishes an update:</p>
                  <ul className="space-y-2 text-foreground/80 list-disc pl-5">
                    <li>Replace the old code in that module with the new version, or</li>
                    <li>Import the new <strong>.bas file</strong> and delete the old module.</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Safe usage card */}
          <div className="p-6 rounded-xl bg-muted/30">
            <h2 className="text-[24px] font-semibold mb-4 text-foreground">Safe usage & good practices</h2>
            <ul className="space-y-2 text-foreground/80 list-disc pl-5">
              <li>Always test new macros on a copy of your workbook first.</li>
              <li>Do a quick scan of the code to make sure it only works with the sheets and ranges you expect.</li>
              <li>Don't share confidential data when posting macros back to MacroVault.</li>
              <li>If a macro no longer matches your process, disable or delete it from the VBA editor.</li>
              <li>When in doubt, ask your team or IT before using a macro in sensitive files.</li>
            </ul>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Docs;
